import { useEffect } from "react";
import config from "../../constants/config";
import TFCookies from "../../enums/tf-cookies";
import requestUrls from "../../utils/requestUrls";
import CookieService from "../../services/cookie.service";
import { getCountry, getLang } from "../../services/localeSupport.service";
import { parseRpDetailsValue } from "../../utils/rpDetails.utils";

const { ENV_SUFIX } = config;

/**
 * Detect whether the current page is part of the MFA flow
 * based on URL path or sessionStorage marker set by MfaProxy.
 */
function isMfaFlow() {
  return (
    window.location.href.includes("mfa") ||
    sessionStorage.getItem("gig_apikey") ===
      process.env[`REACT_APP_MFA_CDC_API_KEY${ENV_SUFIX}`]
  );
}

/**
 * Detect whether the current page is part of the DCR flow
 * based on URL path or sessionStorage marker set by DCRProxy.
 */
function isDcrFlow() {
  return (
    window.location.href.includes("dcr") ||
    sessionStorage.getItem("gig_apikey") ===
      process.env[`REACT_APP_DCR_CDC_API_KEY${ENV_SUFIX}`]
  );
}

/**
 * Return the correct CDC API key for the current flow.
 * DCR pages use a separate Gigya site (DCR key);
 * MFA pages use a separate Gigya site (MFA key);
 * everything else uses the regular key.
 */
function getFlowApiKey() {
  if (isDcrFlow()) {
    return (
      process.env[`REACT_APP_DCR_CDC_API_KEY${ENV_SUFIX}`] ||
      process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`]
    );
  }
  if (isMfaFlow()) {
    return (
      process.env[`REACT_APP_MFA_CDC_API_KEY${ENV_SUFIX}`] ||
      process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`]
    );
  }
  return process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`];
}

/**
 * Resolve the domain-based fallback auth/login URL from requestUrls.
 */
function getDomainFallbackUrl() {
  const serviceUrl = process.env[`REACT_APP_SERVICE_URL${ENV_SUFIX}`];
  const returnUrl = `${serviceUrl}/${getCountry().toLowerCase()}/${getLang().toLowerCase()}/home.html`;

  return `${serviceUrl}/auth/login?returnUrl=${returnUrl}`;
}

function getCloudDomainFallbackUrl() {
  const domainKey = window.location.host.includes("www")
    ? window.location.host.split(".")[1]
    : window.location.host.split(".")[0];

  return requestUrls.cloudRedirect[domainKey] || null;
}

function isLoopProneUrl(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/account-center/proxy.html") ||
    url.includes("/account-center/cloud-proxy.html") ||
    url.includes("/account-center/MFAproxy.html") ||
    url.includes("/account-center/DCRproxy.html")
  );
}

function getSafeReferrerState() {
  const referrerState = sessionStorage.getItem("referrer_state");
  return referrerState && !isLoopProneUrl(referrerState) ? referrerState : null;
}

function redirectToOIDC() {
  try {
    const cookieValue = sessionStorage.getItem(TFCookies.CODE.CIP_RPDETAILS);

    if (cookieValue) {
      try {
        const rpDetailsObject = parseRpDetailsValue(cookieValue);

        if (!rpDetailsObject) {
          return;
        }

        // Guard against redirect loops: if the target URL contains "MFAproxy"/"DCRproxy"
        // or the current proxy page we are already on, skip the redirect.
        const targetUrl = rpDetailsObject?.loginUrl || "";
        if (
          isMfaFlow() &&
          (targetUrl.includes("MFAproxy") ||
            targetUrl.includes("mfa-signin-identifier"))
        ) {
          console.warn(
            "redirectToOIDC: skipping redirect to avoid MFA loop —",
            targetUrl,
          );
          return;
        }
        if (isDcrFlow() && targetUrl.includes("DCRproxy")) {
          console.warn(
            "redirectToOIDC: skipping redirect to avoid DCR loop —",
            targetUrl,
          );
          return;
        }

        const rpClientId = CookieService.getCookie("rp_clientid");
        const referrerState = getSafeReferrerState();

        if (rpDetailsObject?.client_id) {
          if (rpClientId !== rpDetailsObject.client_id) {
            // Client ID mismatch — the stored session belongs to a different RP.
            // Honour referrer_state if present, otherwise fall back to the domain URL.
            console.warn(
              "redirectToOIDC: rp_clientid mismatch — redirecting to referrer_state or domain fallback",
            );
            window.location.href = referrerState || getDomainFallbackUrl();
          } else if (rpDetailsObject.loginUrl) {
            window.location.href = rpDetailsObject.loginUrl;
          } else if (referrerState) {
            window.location.href = referrerState;
          } else {
            window.location.href = getDomainFallbackUrl();
          }
          return;
        } else {
          window.location.href = getDomainFallbackUrl();
        }
      } catch (error) {
        console.error("Error parsing cip_rpDetails in redirectToOIDC:", error);
      }
    } else {
      // cip_rpDetails is absent — first load with no active OIDC session
      // (e.g. bookmarked or direct navigation before any proxy sets the cookie).
      // Fall back to the domain auth URL to re-initiate the OIDC flow.
      console.warn(
        "redirectToOIDC: cip_rpDetails not found — redirecting to domain fallback",
      );
      const fallbackUrl = getDomainFallbackUrl();
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      } else {
        console.error("redirectToOIDC: no fallback URL found for this domain");
      }
    }
  } catch (error) {
    console.error("Error in redirectToOIDC:", error);
  }
}

function redirectToSAML() {
  try {
    const cookieValue = sessionStorage.getItem(TFCookies.CODE.CIP_RPDETAILS);

    if (cookieValue) {
      try {
        const rpDetailsObject = parseRpDetailsValue(cookieValue);

        if (!rpDetailsObject) {
          return;
        }

        const rpClientId = CookieService.getCookie("rp_clientid");
        const referrerState = getSafeReferrerState();

        if (rpDetailsObject?.client_id) {
          if (rpClientId !== rpDetailsObject.client_id) {
            // Client ID mismatch — the stored session belongs to a different RP.
            // Honour referrer_state if present, otherwise fall back to the domain URL.
            console.warn(
              "redirectToSAML: rp_clientid mismatch — redirecting to referrer_state or domain fallback",
            );
            window.location.href = referrerState || getCloudDomainFallbackUrl();
          } else if (rpDetailsObject.loginUrl) {
            window.location.href = rpDetailsObject.loginUrl;
          } else if (referrerState) {
            window.location.href = referrerState;
          } else {
            window.location.href = getCloudDomainFallbackUrl();
          }
          return;
        } else {
          window.location.href = getCloudDomainFallbackUrl();
        }
      } catch (error) {
        console.error("Error parsing cip_rpDetails in redirectToSAML:", error);
      }
    } else {
      // cip_rpDetails is absent — first load with no active SAML session
      // (e.g. bookmarked or direct navigation before any proxy sets the cookie).
      // Fall back to the domain auth URL to re-initiate the SAML flow.
      console.warn(
        "redirectToSAML: cip_rpDetails not found — redirecting to domain fallback",
      );
      const fallbackUrl = getCloudDomainFallbackUrl();
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      } else {
        console.error("redirectToSAML: no fallback URL found for this domain");
      }
    }
  } catch (error) {
    console.error("Error in redirectToSAML:", error);
  }
}

/**
 * Pure data extractor — reads gigyaCache and returns the OIDC clientID
 * for the current flow. Does NOT trigger any redirects; session guarding
 * is handled exclusively by useNoSessionGuard.
 *
 * @returns {{ oidcClientId: string|null, spName: string|null }}
 */
export const getOIDCContext = () => {
  const cookieValue = sessionStorage.getItem("gigyaCache");
  let oidcClientId = null;
  let spName = null;

  if (cookieValue && cookieValue !== "{}") {
    try {
      const cookieObj = JSON.parse(cookieValue);

      // Use the flow-specific API key (MFA vs regular)
      const API_KEY = getFlowApiKey();
      const key = `gig_oidcContext_${API_KEY}`;
      let cookieData = cookieObj[key]?.response;

      // Fallback: if not found, try the gig_apikey stored by the proxy page
      if (!cookieData) {
        const storedApiKey = sessionStorage.getItem("gig_apikey");
        if (storedApiKey && storedApiKey !== API_KEY) {
          const fallbackKey = `gig_oidcContext_${storedApiKey}`;
          cookieData = cookieObj[fallbackKey]?.response;
        }
      }

      oidcClientId = cookieData?.[0]?.clientID;
      spName = cookieData?.[0]?.state;
    } catch (error) {
      console.error("getOIDCContext: error parsing gigyaCache:", error);
    }
  }

  return { oidcClientId, spName };
};

/**
 * React hook — the sole owner of the "no active OIDC session" guard.
 * Call this at the top of any sign-in page component.
 *
 * @param {boolean} [enabled=true] - Pass false to skip the guard (e.g. cloud/SAML flows).
 *
 * On mount (when enabled):
 *   - Delegates to getOIDCContext() to extract the clientID.
 *   - If no clientID is found (bookmark / direct navigation / expired session)
 *     it immediately redirects to re-initiate the OIDC flow.
 *   - Schedules a 48-second safety redirect in case the session expires
 *     while the user is on the page.
 */
export const useNoSessionGuard = (enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    try {
      const { oidcClientId } = getOIDCContext();

      if (!oidcClientId) {
        console.warn(
          "useNoSessionGuard: no OIDC context — redirecting to re-initiate OIDC flow",
        );
        redirectToOIDC();
        return;
      }

      // Safety net: redirect after 48s in case the session expires on-page.
      const timeoutId = setTimeout(() => {
        console.warn(
          "useNoSessionGuard: session timeout — redirecting to re-initiate OIDC flow",
        );
        redirectToOIDC();
      }, 480000);

      return () => clearTimeout(timeoutId);
    } catch (error) {
      console.error("useNoSessionGuard error:", error);
      redirectToOIDC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};

export const getSAMLContext = () => {
  const cookieValue = sessionStorage.getItem("gigyaCache");
  let samlClientId = null;
  if (cookieValue && cookieValue !== "{}") {
    try {
      const cookieObj = JSON.parse(cookieValue);
      const API_KEY = process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`];
      const key = `gig_samlContext_${API_KEY}`;
      const cookieData = cookieObj[key]?.response;
      samlClientId = cookieData?.[0]?.samlContext;
    } catch (error) {
      console.error(
        "Error parsing gigyaCache or accessing cookie data:",
        error,
      );
    }
  }
  if (!samlClientId) {
    redirectToSAML();
  }

  // setTimeout(() => {
  //   redirectToSAML();
  // }, 480000);

  return samlClientId;
};
